// Outbox queue backed by IndexedDB.
import { getDB } from "./db";

export const STATUS = {
  PENDING: "pending",
  IN_FLIGHT: "in_flight",
  SUCCESS: "success",
  FAILED_FINAL: "failed_final",
};

export async function enqueue({ method, path, body, headers = {}, entity_type = null,
                                owner_user_id = null, idempotency_key }) {
  const db = await getDB();
  const row = {
    method, path, body, headers, entity_type, owner_user_id,
    idempotency_key,
    status: STATUS.PENDING,
    enqueuedAt: new Date().toISOString(),
    attemptCount: 0,
    lastError: null,
  };
  await db.add("outbox", row);
  return row;
}

export async function listByStatus(status) {
  const db = await getDB();
  const idx = db.transaction("outbox").store.index("by_status");
  return await idx.getAll(status);
}

export async function listAll() {
  const db = await getDB();
  return await db.getAll("outbox");
}

export async function listPendingFIFO() {
  const rows = await listByStatus(STATUS.PENDING);
  rows.sort((a, b) => a._id - b._id);
  return rows;
}

export async function updateRow(_id, patch) {
  const db = await getDB();
  const tx = db.transaction("outbox", "readwrite");
  const current = await tx.store.get(_id);
  if (!current) return;
  Object.assign(current, patch);
  await tx.store.put(current);
  await tx.done;
}

export async function markInFlight(_id) {
  await updateRow(_id, { status: STATUS.IN_FLIGHT });
}

export async function markSuccess(_id, response = null) {
  await updateRow(_id, {
    status: STATUS.SUCCESS,
    lastError: null,
    completedAt: new Date().toISOString(),
    response,
  });
}

export async function markFailedFinal(_id, errorText) {
  await updateRow(_id, {
    status: STATUS.FAILED_FINAL,
    lastError: errorText,
    completedAt: new Date().toISOString(),
  });
}

export async function markRetry(_id, errorText, nextAttempt) {
  await updateRow(_id, {
    status: STATUS.PENDING,
    lastError: errorText,
    attemptCount: nextAttempt,
    nextAttemptAt: null,
  });
}

export async function counts() {
  const rows = await listAll();
  const out = { pending: 0, in_flight: 0, success: 0, failed_final: 0, total: rows.length };
  for (const r of rows) out[r.status] = (out[r.status] || 0) + 1;
  return out;
}

export async function retryOne(_id) {
  await updateRow(_id, {
    status: STATUS.PENDING,
    attemptCount: 0,
    lastError: null,
  });
}

export async function retryAllFailed() {
  const rows = await listByStatus(STATUS.FAILED_FINAL);
  for (const r of rows) await retryOne(r._id);
  return rows.length;
}

export async function discard(_id) {
  const db = await getDB();
  await db.delete("outbox", _id);
}

export async function gc(olderThanMs = 24 * 60 * 60 * 1000) {
  const rows = await listByStatus(STATUS.SUCCESS);
  const cutoff = Date.now() - olderThanMs;
  const db = await getDB();
  for (const r of rows) {
    const t = r.completedAt ? new Date(r.completedAt).getTime() : 0;
    if (t && t < cutoff) await db.delete("outbox", r._id);
  }
}

const BACKOFF_MS = [2_000, 8_000, 30_000, 120_000, 600_000];
export const MAX_ATTEMPTS = BACKOFF_MS.length;

export function backoffMs(attempt) {
  return BACKOFF_MS[Math.min(attempt, BACKOFF_MS.length - 1)];
}
